import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { FaSearch, FaCode } from 'react-icons/fa';
import classnames from 'classnames';
import { toast } from '../../toasts';
import contractTemplateService from '../../services/contractTemplate.service';
import { getError } from '../../utils';
import { Button } from '../../components/EntityList';

const CATEGORY_LABELS = { alquiler: 'Alquiler', venta: 'Venta', activo: 'Activo' };

/** Flattens one category's catalog into a searchable list of {token, label, path}. */
function flattenCatalog(catalog) {
	if (!catalog) return [];
	const items = catalog.fields.map((f) => ({
		token: `{{${catalog.root}.${f.key}}}`,
		label: f.label,
		path: `${catalog.label} / ${f.label}`,
	}));
	catalog.sections.forEach((section) => {
		section.fields.forEach((f) => {
			items.push({
				token: `{{${catalog.root}.${section.key}.${f.key}}}`,
				label: f.label,
				path: `${section.label} / ${f.label}`,
			});
		});
	});
	return items;
}

/**
 * Two-level variable picker: left rail selects the root's own fields or a
 * related entity section, right pane lists that section's leaf fields as
 * clickable rows. A search box collapses both levels into one filtered list.
 */
export default function VariablePickerModal({ show, onClose, category, onInsert }) {
	const [catalogByCategory, setCatalogByCategory] = useState(null);
	const [loading, setLoading] = useState(false);
	const [selectedKey, setSelectedKey] = useState('__root__');
	const [query, setQuery] = useState('');

	useEffect(() => {
		if (!show || catalogByCategory || loading) return;
		setLoading(true);
		contractTemplateService
			.getVariables()
			.then((res) => setCatalogByCategory(res.data))
			.catch((err) => toast.error(getError(err)))
			.finally(() => setLoading(false));
	}, [show, catalogByCategory, loading]);

	useEffect(() => {
		if (show) {
			setSelectedKey('__root__');
			setQuery('');
		}
	}, [show]);

	const catalog = catalogByCategory?.[category];

	const flatItems = useMemo(() => flattenCatalog(catalog), [catalog]);
	const filteredFlat = useMemo(() => {
		if (!query.trim()) return null;
		const q = query.trim().toLowerCase();
		return flatItems.filter((i) => i.label.toLowerCase().includes(q) || i.token.toLowerCase().includes(q));
	}, [flatItems, query]);

	const railEntries = useMemo(() => {
		if (!catalog) return [];
		return [
			{ key: '__root__', label: `Campos de ${catalog.label}` },
			...catalog.sections.map((s) => ({ key: s.key, label: s.label })),
		];
	}, [catalog]);

	const selectedFields = useMemo(() => {
		if (!catalog) return [];
		if (selectedKey === '__root__') {
			return catalog.fields.map((f) => ({ token: `{{${catalog.root}.${f.key}}}`, label: f.label }));
		}
		const section = catalog.sections.find((s) => s.key === selectedKey);
		if (!section) return [];
		return section.fields.map((f) => ({ token: `{{${catalog.root}.${section.key}.${f.key}}}`, label: f.label }));
	}, [catalog, selectedKey]);

	const handlePick = (token) => {
		onInsert(token);
		onClose();
	};

	return (
		<BsModal
			show={show}
			onHide={onClose}
			centered
			dialogClassName='tw:max-w-[720px]'
			contentClassName='entity-list-page el-modal tw:rounded-[14px] tw:border-0 tw:overflow-hidden tw:shadow-overlay tw:font-sans tw:text-[14px]/[1.45] tw:text-ink-0'
		>
			<BsModal.Header
				closeButton
				className='tw:items-start tw:border-b tw:border-border tw:px-[22px] tw:pt-[20px] tw:pb-[14px]'
			>
				<div className='tw:flex tw:items-start tw:gap-3'>
					<span className='tw:mt-0.5 tw:flex tw:h-[38px] tw:w-[38px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-[9px] tw:bg-accent-soft tw:text-accent'>
						<FaCode />
					</span>
					<div>
						<BsModal.Title className='tw:text-[17px]/[1.3] tw:font-bold tw:tracking-[-0.01em] tw:text-ink-0'>
							Insertar variable
						</BsModal.Title>
						<p className='tw:mb-0 tw:mt-1 tw:text-[12.5px] tw:text-ink-2'>
							{CATEGORY_LABELS[category] || category}
						</p>
					</div>
				</div>
			</BsModal.Header>

			<BsModal.Body className='tw:flex tw:flex-col tw:gap-3.5 tw:px-[22px] tw:py-[18px]'>
				{loading && !catalog ? (
					<div className='tw:flex tw:flex-col tw:items-center tw:gap-2 tw:py-10 tw:text-center'>
						<div className='tw:h-10 tw:w-10 tw:animate-spin tw:rounded-full tw:border-4 tw:border-surface-3 tw:border-t-accent' />
						<div className='tw:text-[12.5px] tw:text-ink-3'>Cargando variables…</div>
					</div>
				) : !catalog ? (
					<div className='tw:py-6 tw:text-center tw:text-[12.5px] tw:text-ink-3'>
						No hay variables disponibles para esta categoría.
					</div>
				) : (
					<>
						<div className='tw:flex tw:h-[34px] tw:items-center tw:gap-2 tw:rounded-[7px] tw:border tw:border-border tw:bg-surface-2 tw:px-3 tw:text-ink-2'>
							<FaSearch className='tw:text-[12px]' />
							<input
								type='text'
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder='Buscar variable…'
								className='entity-list-search-input tw:w-full tw:border-0 tw:bg-transparent tw:text-[13.5px] tw:text-ink-0 tw:outline-none tw:placeholder:text-ink-3'
							/>
						</div>

						{filteredFlat ? (
							<div className='tw:max-h-[360px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border'>
								{filteredFlat.length === 0 ? (
									<div className='tw:px-3 tw:py-6 tw:text-center tw:text-[12.5px] tw:text-ink-3'>
										Sin resultados
									</div>
								) : (
									filteredFlat.map((item) => (
										<button
											key={item.token}
											type='button'
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => handlePick(item.token)}
											className='tw:flex tw:w-full tw:appearance-none tw:items-center tw:justify-between tw:gap-3 tw:border-t-0 tw:border-x-0 tw:border-b tw:border-border tw:bg-transparent tw:px-3 tw:py-2 tw:text-left tw:last:border-b-0 tw:hover:bg-surface-2'
										>
											<span className='tw:flex tw:flex-col'>
												<span className='tw:text-[13.5px] tw:font-medium tw:text-ink-0'>{item.label}</span>
												<span className='tw:text-[11.5px] tw:text-ink-3'>{item.path}</span>
											</span>
											<span className='tw:shrink-0 tw:font-mono tw:text-[11.5px] tw:text-ink-3'>{item.token}</span>
										</button>
									))
								)}
							</div>
						) : (
							<div className='tw:grid tw:grid-cols-[200px_1fr] tw:gap-4'>
								<div className='tw:flex tw:flex-col tw:gap-1 tw:rounded-token tw:border tw:border-border tw:bg-surface tw:p-1.5'>
									{railEntries.map((entry) => (
										<button
											key={entry.key}
											type='button'
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => setSelectedKey(entry.key)}
											className={classnames(
												'tw:appearance-none tw:border-0 tw:bg-transparent tw:rounded-[6px] tw:px-2.5 tw:py-2 tw:text-left tw:text-[13px] tw:transition-colors tw:duration-100',
												selectedKey === entry.key
													? 'tw:bg-accent-soft tw:text-accent-ink tw:font-medium'
													: 'tw:text-ink-1 tw:hover:bg-surface-2',
											)}
										>
											{entry.label}
										</button>
									))}
								</div>
								<div className='tw:max-h-[360px] tw:overflow-y-auto tw:rounded-token tw:border tw:border-border'>
									{selectedFields.length === 0 ? (
										<div className='tw:px-3 tw:py-6 tw:text-center tw:text-[12.5px] tw:text-ink-3'>
											Sin campos disponibles
										</div>
									) : (
										selectedFields.map((f) => (
											<button
												key={f.token}
												type='button'
												onMouseDown={(e) => e.preventDefault()}
												onClick={() => handlePick(f.token)}
												className='tw:flex tw:w-full tw:appearance-none tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:bg-transparent tw:px-3 tw:py-2 tw:text-left tw:last:border-b-0 tw:hover:bg-surface-2'
											>
												<span className='tw:text-[13.5px] tw:font-medium tw:text-ink-0'>{f.label}</span>
												<span className='tw:font-mono tw:text-[11.5px] tw:text-ink-3'>{f.token}</span>
											</button>
										))
									)}
								</div>
							</div>
						)}
					</>
				)}
			</BsModal.Body>

			<BsModal.Footer className='tw:border-t tw:border-border tw:bg-surface-2 tw:px-[22px] tw:py-[14px] tw:gap-2'>
				<Button variant='default' onClick={onClose}>
					Cancelar
				</Button>
			</BsModal.Footer>
		</BsModal>
	);
}

VariablePickerModal.propTypes = {
	show: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	category: PropTypes.string.isRequired,
	onInsert: PropTypes.func.isRequired,
};
